/**
 * Project storage contract. The editor only talks to a `ProjectStore`; today projects live as a
 * file inside a folder on the user's disk (File System Access API). A cloud or server store can be
 * added later by implementing this interface and wiring it in `projects/setup.ts`.
 */
import type { Project, ProjectSettings } from '@ve/core';

/** A project this device has opened before. */
export interface RecentProject {
  id: string;
  name: string;
  /** Human-readable location, e.g. the folder name. */
  location: string;
  lastOpened: number;
}

/** A location the user picked, before it is opened or a project is created in it. */
export interface PickedLocation {
  /** Display name, e.g. the folder name. */
  name: string;
  /** Whether a project already exists there. */
  hasProject: boolean;
}

export interface NewProject {
  name: string;
  settings: Partial<ProjectSettings>;
}

export type AccessState = 'granted' | 'prompt' | 'denied' | 'missing';

/** A project file the operating system opened the app with (double-click, "Open with"). */
export interface LaunchedProject {
  id: string;
  name: string;
  /** The file's name, e.g. `My Film.cutroom`. */
  fileName: string;
  /**
   * `granted`: opens without asking. `prompt`: its folder is known, access must be confirmed.
   * `missing`: the user must point at the folder that contains the file.
   */
  access: AccessState;
}

export interface ProjectStore {
  readonly id: string;
  isSupported(): boolean;

  listRecent(): Promise<RecentProject[]>;
  forgetRecent(projectId: string): Promise<void>;
  /** Whether a recent project can be opened without a prompt. */
  access(projectId: string): Promise<AccessState>;

  /** Lets the user pick a location (user gesture). Null if cancelled. */
  pickLocation(purpose: 'open' | 'create'): Promise<PickedLocation | null>;
  /** Opens the project in a picked location (it must have one). */
  openAt(location: PickedLocation): Promise<Project>;
  /**
   * Creates a project. With `inPlace`, the project is created directly in the picked location (e.g.
   * a folder that already holds footage); otherwise in a new sub-location named after the project.
   */
  createAt(location: PickedLocation, init: NewProject, options?: { inPlace?: boolean }): Promise<Project>;
  /** Opens a recent project, prompting for access if needed (user gesture). */
  openRecent(projectId: string): Promise<Project>;

  /**
   * Reads a project file handed over by the operating system. Optional: only stores whose projects
   * are files on disk support it.
   */
  inspectLaunched?(file: FileSystemFileHandle): Promise<LaunchedProject>;
  /**
   * Opens a launched project file. Needs a user gesture unless its access is `granted` (permission
   * prompt, or a folder picker to locate it). Null if the user cancelled.
   */
  openLaunched?(file: FileSystemFileHandle): Promise<Project | null>;

  save(project: Project): Promise<void>;

  /**
   * Takes the exclusive editing lock for a project (prevents two tabs overwriting each other).
   * Resolves to a release function, or null if the project is already open elsewhere.
   */
  lock(projectId: string): Promise<(() => void) | null>;
}

export class ProjectStoreError extends Error {
  constructor(
    message: string,
    readonly reason: 'no-access' | 'not-found' | 'invalid' | 'locked',
  ) {
    super(message);
    this.name = 'ProjectStoreError';
  }
}
